import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, Building2, User } from 'lucide-react';
import axios from 'axios';
import { API } from '../constants/site';
import { track } from '../lib/analytics';
import { useLanguage } from '../i18n/LanguageContext';
import { EASE } from './motion';

// text-base (16px) minimum: prevents iOS Safari auto-zoom on input focus
const inputCls =
  'w-full bg-transparent border-b border-line/20 py-3 text-ink placeholder-smoke/60 text-base focus:border-accent focus:outline-none transition-colors duration-300';
const labelCls = 'block text-[10px] font-bold uppercase tracking-[0.2em] text-smoke mb-1';

const detectSource = () => {
  try {
    const utm = (new URLSearchParams(window.location.search).get('utm_source') || '').toLowerCase();
    const ref = (document.referrer || '').toLowerCase();
    const s = utm || ref;
    if (s.includes('google')) return 'google';
    if (s.includes('facebook') || s.includes('fb.com') || s.includes('fbclid')) return 'facebook';
    if (s.includes('instagram')) return 'instagram';
    if (s.includes('tiktok')) return 'tiktok';
    if (ref) return 'altele';
    return 'direct';
  } catch {
    return 'direct';
  }
};

const detectDevice = () => (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobil' : 'desktop');

export const LeadForm = ({ testIdPrefix = 'lead-form', defaultServices = [], measurements = null }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: '', phone: '', email: '', city: '',
    services: defaultServices, client_type: 'fizica', cui: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((e) => ({ ...e, [k]: undefined }));
  };

  const toggleService = (s) =>
    set('services', form.services.includes(s) ? form.services.filter((x) => x !== s) : [...form.services, s]);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = t('form.errRequired');
    if (!form.phone.trim()) errs.phone = t('form.errRequired');
    if (!form.city.trim()) errs.city = t('form.errRequired');
    if (form.services.length === 0) errs.services = t('form.errServices');
    if (form.client_type === 'juridica' && !form.cui.trim()) errs.cui = t('form.errCui');
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name, phone: form.phone, email: form.email, city: form.city,
        services: form.services, client_type: form.client_type,
        cui: form.client_type === 'juridica' ? form.cui || null : null,
        meta: { device: detectDevice(), source: detectSource(), form: testIdPrefix },
        ...(measurements ? { measurements } : {}),
      });
      setSent(true);
      track('generate_lead', {
        form_id: testIdPrefix,
        services: form.services.join(','),
        client_type: form.client_type,
      });
    } catch (err) {
      setError(t('form.errMsg'));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <motion.div
        data-testid={`${testIdPrefix}-success`}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="border border-accent/40 bg-accent/5 p-8 text-center"
      >
        <CheckCircle2 size={44} className="text-accent mx-auto mb-4" />
        <p className="font-display text-2xl font-medium mb-2">{t('form.okTitle')}</p>
        <p className="text-smoke text-sm mb-6">{t('form.okMsg')}</p>
        <button
          data-testid={`${testIdPrefix}-new-request-button`}
          onClick={() => { setSent(false); setForm({ name: '', phone: '', email: '', city: '', services: defaultServices, client_type: 'fizica', cui: '' }); }}
          className="text-xs font-bold uppercase tracking-[0.2em] text-accent border-b border-accent/40 pb-1 hover:border-accent transition-colors"
        >
          {t('form.newRequest')}
        </button>
      </motion.div>
    );
  }

  return (
    <form data-testid={testIdPrefix} onSubmit={submit} className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6">
        <div>
          <label className={labelCls}>{t('form.name')}</label>
          <input data-testid={`${testIdPrefix}-name-input`} className={`${inputCls} ${fieldErrors.name ? 'border-[#EF4444]' : ''}`} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder={t('form.name')} />
          {fieldErrors.name && <p data-testid={`${testIdPrefix}-name-error`} className="text-[#EF4444] text-xs mt-1.5">{fieldErrors.name}</p>}
        </div>
        <div>
          <label className={labelCls}>{t('form.phone')}</label>
          <input type="tel" inputMode="tel" autoComplete="tel" data-testid={`${testIdPrefix}-phone-input`} className={`${inputCls} ${fieldErrors.phone ? 'border-[#EF4444]' : ''}`} value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="07xx xxx xxx" />
          {fieldErrors.phone && <p data-testid={`${testIdPrefix}-phone-error`} className="text-[#EF4444] text-xs mt-1.5">{fieldErrors.phone}</p>}
        </div>
        <div>
          <label className={labelCls}>{t('form.email')}</label>
          <input type="email" data-testid={`${testIdPrefix}-email-input`} className={inputCls} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="email@exemplu.ro" />
        </div>
        <div>
          <label className={labelCls}>{t('form.city')}</label>
          <input data-testid={`${testIdPrefix}-city-input`} className={`${inputCls} ${fieldErrors.city ? 'border-[#EF4444]' : ''}`} value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="Timișoara" />
          {fieldErrors.city && <p data-testid={`${testIdPrefix}-city-error`} className="text-[#EF4444] text-xs mt-1.5">{fieldErrors.city}</p>}
        </div>
      </div>

      <div>
        <label className={labelCls}>{t('form.services')}</label>
        <div className="flex flex-wrap gap-2 mt-2">
          {[['pvc', t('form.svcPvc')], ['garaj', t('form.svcGaraj')], ['automatizari', t('form.svcAuto')], ['rulouri', t('form.svcRulouri')]].map(([key, label]) => (
            <button
              type="button"
              key={key}
              data-testid={`${testIdPrefix}-service-${key}`}
              onClick={() => toggleService(key)}
              className={`px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] border rounded-full transition-all duration-300 ${
                form.services.includes(key)
                  ? 'bg-accent text-white border-accent'
                  : 'border-line/20 text-smoke hover:border-accent/60 hover:text-ink'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {fieldErrors.services && <p data-testid={`${testIdPrefix}-services-error`} className="text-[#EF4444] text-xs mt-2">{fieldErrors.services}</p>}
      </div>

      <div>
        <label className={labelCls}>{t('form.clientType')}</label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {[['fizica', t('form.fizica'), User], ['juridica', t('form.juridica'), Building2]].map(([val, label, Icon]) => (
            <button
              type="button"
              key={val}
              data-testid={`${testIdPrefix}-type-${val}`}
              onClick={() => set('client_type', val)}
              className={`flex items-center justify-center gap-2 px-4 py-3.5 text-xs font-bold uppercase tracking-[0.12em] border rounded-xl transition-all duration-300 ${
                form.client_type === val
                  ? 'bg-ink/5 text-ink border-accent'
                  : 'border-line/20 text-smoke hover:border-line/50'
              }`}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {form.client_type === 'juridica' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="overflow-hidden"
          >
            <label className={labelCls}>{t('form.cui')}</label>
            <input data-testid={`${testIdPrefix}-cui-input`} className={`${inputCls} ${fieldErrors.cui ? 'border-[#EF4444]' : ''}`} value={form.cui} onChange={(e) => set('cui', e.target.value)} placeholder="RO12345678" />
            {fieldErrors.cui && <p data-testid={`${testIdPrefix}-cui-error`} className="text-[#EF4444] text-xs mt-1.5">{fieldErrors.cui}</p>}
          </motion.div>
        )}
      </AnimatePresence>

      {error && <p data-testid={`${testIdPrefix}-error`} className="text-[#EF4444] text-xs">{error}</p>}

      <button
        type="submit"
        disabled={sending}
        data-testid={`${testIdPrefix}-submit`}
        className="w-full bg-accent text-white font-bold text-sm uppercase tracking-[0.2em] py-4 rounded-full hover:bg-accenth hover:shadow-[0_12px_35px_rgba(200,16,46,0.3)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60"
      >
        {sending ? t('form.sending') : t('form.submit')}
      </button>
    </form>
  );
};
